import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { ICliente } from '@model/Cliente';
import { IClienteSaldo } from '@model/ClienteSaldo';
import { EnumEmpresa, IComboEmpresa } from '@model/Empresa';
import { EnumTipoSaldo, useClienteService } from '@services/ClienteService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { SaldoRegistrarForm } from './SaldoRegistrarForm';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';

export type SaldoRegistrarModalProps = {
    modal: IModalWindow
    cliente: ICliente
    empresaCombo: IComboEmpresa
    tipoSaldo: EnumTipoSaldo
    onSaldoRegistrado: (response: AxiosResponse<IClienteSaldo>) => void
}

export function SaldoRegistrarModal ({ modal, cliente, empresaCombo, tipoSaldo, onSaldoRegistrado }: SaldoRegistrarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const clienteService = useClienteService();

    const form = useForm<Partial<IClienteSaldo>>({
        empresaId: useField(EnumEmpresa.None),
        descricao: useField(),
        valor: useField()
    })

    const validation = useValidation<IClienteSaldo>({
        empresaId: yup.number().required(),
        descricao: yup.string().required(),
        valor: yup.number().required().positive()
    })

    const isDebito = React.useMemo(() => tipoSaldo === EnumTipoSaldo.Debito, [tipoSaldo]);

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();
            validation.validate(values)
                .then(() => {
                    loading.start();
                    clienteService.registrarSaldo(cliente.clienteId, tipoSaldo, values)
                        .then(response => {
                            message.success(`${isDebito ? "Débito" : "Crédito"} registrada com sucesso`);
                            modal.close();
                            onSaldoRegistrado(response);
                        })
                        .catch(form.setErrorsFromServer)
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [cliente.clienteId, clienteService, form, isDebito, loading, message, modal, onSaldoRegistrado, tipoSaldo, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ModalWindow 
            modal={modal}
            size="md"
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                icon={isDebito ? <RemoveCircleIcon color="secondary" /> : <AddCircleIcon style={{color: 'darkgreen'}} />}
                title={`Registro de ${isDebito ? "Débito" : "Crédito"} Manual`} 
            />
            
            <ModalWindowContent>
                <SaldoRegistrarForm
                    form={form}
                    tipo={tipoSaldo}
                    empresaCombo={empresaCombo}
                />
            </ModalWindowContent>
            
            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Registrar {isDebito ? "Débito" : "Crédito"}</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}
